<template>
    <div>
        <ValidationObserver ref="formModalValidate">
            <b-row class="mt-2">
                <b-col cols="12" md="6">
                    <ValidationProvider name="employee_id" v-slot="{ errors }">
                        <b-form-group :label="$t('employee_id')">
                            <b-form-input v-model="formData.employee_id"
                                          :placeholder="$t('employee_id')"
                                          :state="errors[0] ? false : null" />
                            <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]" />
                        </b-form-group>
                    </ValidationProvider>
                </b-col>
                <b-col cols="12" md="6">
                    <ValidationProvider name="supervisor_id" v-slot="{ errors }">
                        <b-form-group :label="$t('supervisor')">
                            <staff-auto-complete v-model="formData.supervisor_id"
                                                 :value-object="supervisorObject"
                                                 return-value="user_id"
                                                 :validate-error="errors[0]" />
                        </b-form-group>
                    </ValidationProvider>
                </b-col>
                <b-col cols="12" md="6">
                    <ValidationProvider name="budgetary_department" v-slot="{ errors }">
                        <b-form-group :label="$t('hr_budgetary_department')">
                            <parameter-selectbox v-model="formData.budgetary_department"
                                                 code="hr_budgetary_departments"
                                                 :validate-error="errors[0]" />
                        </b-form-group>
                    </ValidationProvider>
                </b-col>
                <b-col cols="12" md="6">
                    <ValidationProvider name="job_title" v-slot="{ errors }">
                        <b-form-group :label="$t('job_title')">
                            <multiselect v-model="selectedJobTitle"
                                         :options="jobTitleOptions"
                                         label="text"
                                         track-by="value"
                                         :placeholder="$t('select')"
                                         :disabled="!formData.budgetary_department"
                                         :class="errors[0] ? 'box-border-color' : ''"
                                         :select-label="''"
                                         :selected-label="''"
                                         :deselect-label="''"
                                         @input="formData.job_title = $event ? $event.value : null">
                                <span slot="noOptions">{{ $t('no_options') }}</span>
                            </multiselect>
                            <span class="invalid-feedback-custom" v-show="errors[0]" v-html="errors[0]" />
                        </b-form-group>
                    </ValidationProvider>
                </b-col>
                <b-col cols="12" md="6">
                    <b-form-group :label="$t('leadership_team')">
                        <b-form-checkbox v-model="formData.is_leadership_team" switch>
                            {{ formData.is_leadership_team ? $t('yes') : $t('no') }}
                        </b-form-checkbox>
                    </b-form-group>
                </b-col>
                <b-col cols="12" md="6">
                    <b-form-group :label="$t('purchase_officer')">
                        <b-form-checkbox v-model="formData.is_purchase_officer" switch>
                            {{ formData.is_purchase_officer ? $t('yes') : $t('no') }}
                        </b-form-checkbox>
                    </b-form-group>
                </b-col>
            </b-row>
            <div class="d-flex justify-content-center mt-3 mb-3">
                <b-button variant="primary"
                          @click="saveForm"
                          :disabled="formLoading">
                    {{ $t('save') }}
                </b-button>
            </div>
        </ValidationObserver>
    </div>
</template>
<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import StaffService from '@/services/StaffService';
import ParameterService from '@/services/ParameterService';
import StaffAutoComplete from '@/components/interactive-fields/StaffAutoComplete.vue';

export default {
    components: {
        ValidationProvider,
        ValidationObserver,
        StaffAutoComplete
    },
    props: {
        staffInformation: {
            type: Object,
            default: null
        }
    },
    watch: {
        'formData.budgetary_department'(newVal, oldVal) {
            if (newVal !== oldVal) {
                if (oldVal !== null) {
                    this.formData.job_title = null;
                    this.selectedJobTitle = null;
                }
                if (newVal) {
                    this.fetchJobTitles(newVal);
                } else {
                    this.jobTitleOptions = [];
                }
            }
        }
    },
    created() {
        this.formId = this.$route.params.id;
        this.formData.employee_id = this.staffInformation.employee_id;
        this.formData.job_title = this.staffInformation.job_title;
        this.formData.supervisor_id = this.staffInformation.supervisor_id;
        this.formData.budgetary_department = this.staffInformation.budgetary_department;
        this.formData.is_leadership_team = this.staffInformation.is_leadership_team || false;
        this.formData.is_purchase_officer = this.staffInformation.is_purchase_officer || false;

        if (this.staffInformation.supervisor_id && this.staffInformation.supervisor_name) {
            this.supervisorObject = {
                value: this.staffInformation.supervisor_id,
                text: this.staffInformation.supervisor_name
            };
        }

        if (this.formData.budgetary_department) {
            this.fetchJobTitles(this.formData.budgetary_department, this.formData.job_title);
        }
    },
    data() {
        return {
            formId: null,
            formLoading: false,
            supervisorObject: null,
            jobTitleOptions: [],
            selectedJobTitle: null,
            formData: {
                employee_id: null,
                job_title: null,
                supervisor_id: null,
                budgetary_department: null,
                is_leadership_team: false,
                is_purchase_officer: false
            }
        }
    },
    methods: {
        fetchJobTitles(departmentCode, currentJobTitle = null) {
            ParameterService.getItems('hr_budgetary_departments')
                .then(response => {
                    const items = response.data.data.items || [];
                    const dept = items.find(item => item.code === departmentCode);
                    if (dept && dept.data) {
                        const parsed = typeof dept.data === 'string' ? JSON.parse(dept.data) : dept.data;
                        this.jobTitleOptions = Object.entries(parsed).map(([key, label]) => ({
                            value: key,
                            text: label
                        }));
                        if (currentJobTitle) {
                            this.selectedJobTitle = this.jobTitleOptions.find(o => o.value === currentJobTitle) || null;
                        }
                    }
                })
                .catch(() => {});
        },
        async saveForm() {
            const isValid = await this.$refs.formModalValidate.validate();
            if (isValid && this.formId) {
                this.formLoading = true;
                const payload = {
                    ...this.formData,
                    is_leadership_team: this.formData.is_leadership_team ? 1 : 0,
                    is_purchase_officer: this.formData.is_purchase_officer ? 1 : 0,
                    name: this.staffInformation.name,
                    surname: this.staffInformation.surname,
                    username: this.staffInformation.username,
                    working_status: this.staffInformation.working_status,
                    contract_type: this.staffInformation.contract_type,
                };

                StaffService.update(this.formId, payload)
                    .then(response => {
                        this.$toast.success(this.$t('api.' + response.data.message));
                        this.$emit('updateFormSuccess', true);
                    })
                    .catch(e => {
                        this.showErrors(e, this.$refs.formModalValidate);
                    })
                    .finally(() => {
                        this.formLoading = false;
                    });
            }
        }
    }
}
</script>
